import React from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import ResumeAnswer from './ResumeAnswer';
import { resumeFromApplication } from './shared';

const emptyAnswers = Object.freeze({
    workStudyAmount: '',
    resume: null,
});

// Whether the student has a work-study award is the Eligibility step's
// campus employment question, asked once for every program. SI asks only for
// the award amount, and only of a student who has one.
function Questions({ answers, onChange }) {
    return (
        <div>
            <h2 className="h5 mb-3">SI-Specific Details</h2>
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Header className="bg-success text-white">
                    <strong>SI - Supplemental Instruction Leader</strong>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="sap-work-study-award-amount"
                            >
                                <Form.Label>
                                    Work-study award amount ($), if you have an
                                    award
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="e.g., 2500.00"
                                    value={answers.workStudyAmount}
                                    onChange={(e) =>
                                        onChange(
                                            'workStudyAmount',
                                            e.target.value
                                        )
                                    }
                                />
                                <Form.Text className="text-muted">
                                    Enter the total work-study amount from your
                                    financial aid award. Leave it empty if you
                                    have none.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </div>
    );
}

// The resume is shown for SI as for LA. `fetchResume` is how the reader
// downloads it (faculty download through the candidacy).
function Summary({ application, fetchResume }) {
    const details = application.siDetails || {};
    const showAward = details.workStudyEligible && details.workStudyAmount;
    if (!showAward && !application.resume) return null;
    return (
        <Card className="mb-3 border-0 shadow-sm">
            <Card.Header className="bg-light">
                <strong>SI Details</strong>
            </Card.Header>
            <Card.Body>
                {showAward && (
                    <p className="mb-2">
                        <strong>Work-study award amount:</strong> $
                        {details.workStudyAmount}
                    </p>
                )}
                <div>
                    <strong>Resume:</strong>{' '}
                    <ResumeAnswer
                        application={application}
                        fetchResume={fetchResume}
                    />
                </div>
            </Card.Body>
        </Card>
    );
}

const siProgram = {
    key: 'si',
    roleName: 'SI Leader',
    article: 'an',
    label: 'SI',
    full: 'Supplemental Instruction Leader',
    bg: 'success',
    description:
        'Lead peer study sessions for historically difficult courses. SI Leaders attend class, prepare session plans, and facilitate collaborative learning outside of lecture.',
    noteVariant: 'success',
    note: (
        <>
            <strong>SI Leader:</strong> You will attend all class sessions, plan
            and facilitate 2-3 weekly study sessions, and collaborate with the
            course instructor. SI Leaders must have previously earned an A or B
            in the course they support.
        </>
    ),
    steps: [
        { label: 'Choose Program', blocks: ['program'] },
        {
            label: 'About You',
            blocks: ['aboutYou', 'priorExperience'],
        },
        {
            label: 'Open Positions',
            blocks: [
                'positions',
                'requirementAnswers',
                'positionCommitments',
                'generalPool',
            ],
        },
        {
            label: 'Interest & Availability',
            blocks: ['interestStatement', 'availability'],
        },
        { label: 'Program Details', blocks: ['programQuestions', 'resume'] },
        { label: 'Eligibility', blocks: ['eligibility', 'campusEmployment'] },
        { label: 'Review & Submit', blocks: ['review', 'references'] },
    ],
    Questions,
    Summary,
    emptyAnswers,
    fromApplication: (application) => ({
        workStudyAmount:
            application?.siDetails?.workStudyAmount != null
                ? String(application.siDetails.workStudyAmount)
                : '',
        resume: resumeFromApplication(application),
    }),
    // `form` is the whole wizard's answers: eligibility is the campus
    // employment answer, so it is never asked twice.
    toPayload: (answers, form) => {
        const eligible = form?.campusEmployment?.workStudy === 'yes';
        return {
            siDetails: {
                workStudyEligible: eligible,
                workStudyAmount:
                    eligible && answers.workStudyAmount
                        ? parseFloat(answers.workStudyAmount)
                        : undefined,
            },
        };
    },
    getErrors: () => [],
};

export default siProgram;
